/**
 * Chord utility functions for voice leading and analysis.
 * Extracted from non-chord-tone-analyzer after melody-first workflow was removed.
 */

import { Note, Chord as TonalChord } from 'tonal';
import type { Chord } from '@/types/chord';

/**
 * Get pitch classes from a chord
 * Builds a chord name from root and quality, then gets pitches using Tonal
 */
export function getChordPitchClasses(chord: Chord): string[] {
  try {
    // Build chord name from scale degree
    const chordRootMap: Record<number, string> = {
      1: chord.key,
      2: Note.transpose(chord.key, '2M'),
      3: Note.transpose(chord.key, '3M'),
      4: Note.transpose(chord.key, '4P'),
      5: Note.transpose(chord.key, '5P'),
      6: Note.transpose(chord.key, '6M'),
      7: Note.transpose(chord.key, '7M'),
    };

    const root = chordRootMap[chord.scaleDegree] || chord.key;

    // Map quality to Tonal chord symbol
    const qualityMap: Record<string, string> = {
      major: '',
      minor: 'm',
      diminished: 'dim',
      augmented: 'aug',
      dom7: '7',
      maj7: 'maj7',
      min7: 'm7',
      halfdim7: 'm7b5',
      dim7: 'dim7',
      dom9: '9',
      maj9: 'maj9',
      min9: 'm9',
      dom11: '11',
      min11: 'm11',
      dom13: '13',
      maj13: 'maj13',
      min13: 'm13',
      alt: '7alt',
      dom7b9: '7b9',
      dom7sharp9: '7#9',
      dom7sharp11: '7#11',
    };

    const qualitySuffix = qualityMap[chord.quality] || '';
    const chordName = `${root}${qualitySuffix}`;

    const tonalChord = TonalChord.get(chordName);

    if (!tonalChord || !tonalChord.notes) {
      return [];
    }

    // Extract pitch classes (remove octave)
    return tonalChord.notes.map(note => {
      const pc = Note.pitchClass(note);
      return pc;
    });
  } catch (error) {
    console.warn('Error getting chord pitch classes:', error);
    return [];
  }
}

/**
 * Find the chord active at a given beat
 */
export function findChordAtBeat(beat: number, sortedChords: Chord[]): Chord | null {
  for (const chord of sortedChords) {
    if (beat >= chord.startBeat && beat < chord.startBeat + chord.duration) {
      return chord;
    }
  }
  return null;
}
